/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import type {
  HashicorpCloudNetwork20200907DNSForwarding as DNSForwarding,
  HashicorpCloudNetwork20200907DNSForwardingRule as DNSForwardingRule,
  HashicorpCloudLocationLink as LocationLink,
} from '@clients/cloud-network';
import type {
  HashicorpCloudResourcemanagerOrganization as Organization,
  HashicorpCloudResourcemanagerProject as Project,
} from '@clients/cloud-resource-manager';
import type ApiService from 'api/services/api';
import type OperationService from 'core/services/operation';

interface Args {
  model: {
    dnsForwardings: DNSForwarding[];
    dnsForwardingRules: DNSForwardingRule[];
    network: LocationLink;
    organization: Organization;
    project: Project;
  };
}

const MAXIMUM_DNS_FORWARDING_RULES = 5;

export default class PageHvnDnsForwardingCreateComponent extends Component<Args> {
  @service declare readonly api: ApiService;
  @service declare readonly flashMessages: FlashMessagesService;
  @service declare readonly intl: IntlService;
  @service declare readonly operation: OperationService;

  @tracked activeRuleToDelete: DNSForwardingRule | null = null;
  @tracked allowDelete = false;

  @action openDeleteModal(rule: DNSForwardingRule) {
    this.activeRuleToDelete = rule;
  }

  @action onCloseDeleteModal() {
    this.activeRuleToDelete = null;
  }

  get hasMaximumNumberOfRules() {
    const rulesLength = this.args.model.dnsForwardingRules.length;
    return rulesLength >= MAXIMUM_DNS_FORWARDING_RULES;
  }

  deleteRule = task(async () => {
    if (!this.activeRuleToDelete) {
      return;
    }
    const { dnsForwardings, network, organization, project } = this.args.model;
    const dnsForwarding = dnsForwardings[0];
    const { operation } = await this.api.network.deleteDNSForwardingRule(
      organization.id!,
      project.id!,
      network.id!,
      dnsForwarding!.id!,
      this.activeRuleToDelete.rule!.id!
    );

    this.operation.addOperation(operation);

    this.flashMessages.success(
      this.intl.t(
        'components.page.dns-forwarding-rules.list.flash-message.delete',
        { domain: this.activeRuleToDelete.rule?.domainName }
      )
    );
    this.activeRuleToDelete = null;
  });
}
